/**
 * The portal chrome's own text: pre-login pages and the shell around every feature.
 *
 * Everything a feature screen shows comes from its backend feature schema (CLAUDE.md, Agent
 * Mistakes Log #1). This catalogue holds only what cannot: the login and password pages, which
 * run before anyone is logged in and so cannot call `/config/resolve`, and the shell's own
 * controls. It follows the Angular portals' precedent (`platform/shared/i18n` and the inline
 * translations of the reset-password components), whose wording is reused here wherever it
 * existed. See docs/dealer-portal-react/shell.md.
 *
 * `{name}`-style placeholders are filled by `fill()`.
 */

export type ShellLocale = 'dk' | 'en' | 'no';

export interface ShellTexts {
  /** `orgLogoAlt`: `{name}` — the selected org node's logo next to the brand's */
  brand: { portalName: string; logoAlt: string; orgLogoAlt: string };
  feedback: { loading: string; errorMessage: string; retry: string };
  login: {
    title: string;
    email: string;
    emailPlaceholder: string;
    password: string;
    passwordPlaceholder: string;
    emailInvalid: string;
    passwordRequired: string;
    submit: string;
    submitting: string;
    forgotPassword: string;
    wrongCredentials: string;
    accountLocked: string;
    failed: string;
    passwordResetDone: string;
    /** `{brand}` — the muted line under the logo */
    subtitle: string;
    /** The optional sign-up pane next to the login card; `{brand}` */
    signUpTitle: string;
    signUpText: string;
    signUpAction: string;
  };
  forgotPassword: {
    title: string;
    subtitle: string;
    email: string;
    emailPlaceholder: string;
    submit: string;
    submitting: string;
    successTitle: string;
    successMessage: string;
    backToLogin: string;
  };
  resetPassword: {
    title: string;
    changeTitle: string;
    validating: string;
    invalidToken: string;
    tokenExpired: string;
    tokenAlreadyUsed: string;
    invalidLink: string;
    goToLogin: string;
    resetComplete: string;
    changeComplete: string;
    redirectingToLogin: string;
    redirectingToDashboard: string;
    resetHeading: string;
    changeHeading: string;
    welcome: string;
    createPassword: string;
    changeMessage: string;
    currentPassword: string;
    enterCurrentPassword: string;
    newPassword: string;
    enterNewPassword: string;
    confirmPassword: string;
    confirmPasswordPlaceholder: string;
    tooShort: string;
    mismatch: string;
    resetting: string;
    changing: string;
    resetButton: string;
    changeButton: string;
    wrongCurrentPassword: string;
    failed: string;
  };
  unauthorized: { title: string; message: string; goBack: string; goHome: string };
  unavailable: {
    title: string;
    tenantDisabled: string;
    orgNodeDisabled: string;
    unknown: string;
    goHome: string;
  };
  notFound: { title: string; message: string; goHome: string };
  legacy: { title: string; message: string; open: string; notLinked: string };
  header: {
    /** The user menu's switch for a GLOBAL admin to see the node as its users do */
    scopedView: string;
    userMenu: string;
    /** The user menu's header row (legacy "Account") */
    account: string;
  };
  breadcrumb: { label: string; home: string };
  /** `{year}`, `{brand}` */
  footer: { copyright: string; poweredBy: string };
  orgNodeSelector: {
    selectOrganization: string;
    organization: string;
    expand: string;
    collapse: string;
    loading: string;
    /** The scope card's caption over the node's name */
    showingDataFor: string;
    types: { ENTERPRISE: string; DEALER: string; GROUP: string; DEPARTMENT: string };
  };
  sidebar: {
    navigation: string;
    hide: string;
    show: string;
    navigateTo: string;
    opensNewTab: string;
    /** The group of entries the user pinned */
    pinned: string;
    /** `{label}` */
    pin: string;
    /** `{label}` */
    unpin: string;
    /** `{label}`, `{count}` — an entry's waiting-items badge, spelled out */
    badge: string;
    /** `{label}`, `{section}` — a pinned entry named with the section it belongs to */
    inSection: string;
  };
  dashboard: { welcomeTitle: string; welcomeSubtitle: string };
  profile: { changePassword: string; testCustomers: string; testCustomersMoved: string };
  kit: {
    yes: string;
    no: string;
    noData: string;
    rowsPerPage: string;
    /** `{start}`, `{end}`, `{total}` */
    range: string;
    /** `{start}`, `{end}` — when the total is unknown */
    rangeOpen: string;
    pagination: string;
    firstPage: string;
    previousPage: string;
    nextPage: string;
    lastPage: string;
    selectAll: string;
    selectRow: string;
    actions: string;
    search: string;
    clearSearch: string;
    remove: string;
    close: string;
    cancel: string;
    save: string;
    saving: string;
    /** Default confirm label of a `danger` modal */
    delete: string;
    /** Collapsible card switch: fold the body away / bring it back (legacy `LUK` / `VIS`) */
    hide: string;
    show: string;
    /** Toasts: the region's name, and the hidden button that closes one */
    notifications: string;
    dismiss: string;
    expandGroup: string;
    collapseGroup: string;
    /** `{selected}`, `{total}` */
    selectedCount: string;
    noMatches: string;
    /** `{count}` — one checklist row standing for several identical records */
    mergedCount: string;
    validation: {
      required: string;
      email: string;
      /** `{n}` */
      minLength: string;
      maxLength: string;
      min: string;
      max: string;
      pattern: string;
      invalid: string;
    };
  };
}

export const SHELL_TEXTS: Record<ShellLocale, ShellTexts> = {
  dk: {
    brand: { portalName: 'Forhandlerportal', logoAlt: 'MiCar', orgLogoAlt: 'Logo for {name}' },
    feedback: {
      loading: 'Indlæser...',
      errorMessage: 'Der opstod en fejl. Prøv venligst igen.',
      retry: 'Prøv igen',
    },
    login: {
      title: 'Log ind',
      email: 'E-mail',
      emailPlaceholder: 'E-mail',
      password: 'Adgangskode',
      passwordPlaceholder: 'Adgangskode',
      emailInvalid: 'Indtast en gyldig e-mailadresse',
      passwordRequired: 'Adgangskode er påkrævet',
      submit: 'Log ind',
      submitting: 'Logger ind...',
      forgotPassword: 'Glemt adgangskode?',
      wrongCredentials: 'Forkert e-mail eller adgangskode.',
      accountLocked: 'Kontoen er låst. Kontakt din administrator.',
      failed: 'Der opstod en fejl. Prøv venligst igen.',
      passwordResetDone: 'Din adgangskode er nulstillet. Log ind med den nye adgangskode.',
      subtitle: 'Log ind til din {brand} forhandlerportal',
      signUpTitle: 'Sign up',
      signUpText: 'Kontakt os for at blive en af de forhandlere der har glæde af {brand}',
      signUpAction: 'Tag kontakt nu',
    },
    forgotPassword: {
      title: 'Nulstil adgangskode',
      subtitle:
        'Indtast din e-mailadresse, og vi sender dig et link til at nulstille din adgangskode.',
      email: 'E-mailadresse',
      emailPlaceholder: 'Indtast din e-mail',
      submit: 'Send nulstillingslink',
      submitting: 'Sender...',
      successTitle: 'Tjek din e-mail',
      successMessage:
        'Hvis der findes en konto med den e-mail, har vi sendt instruktioner til nulstilling af adgangskode.',
      backToLogin: 'Tilbage til login',
    },
    resetPassword: {
      title: 'Nulstil adgangskode',
      changeTitle: 'Skift adgangskode',
      validating: 'Validerer nulstillingslink...',
      invalidToken: 'Ugyldigt nulstillingslink',
      tokenExpired:
        'Dette link til nulstilling af adgangskode er udløbet. Anmod venligst om et nyt.',
      tokenAlreadyUsed:
        'Dette link til nulstilling af adgangskode er allerede blevet brugt. Anmod venligst om et nyt.',
      invalidLink:
        'Dette link til nulstilling af adgangskode er ugyldigt eller er blevet tilbagekaldt. Anmod venligst om et nyt.',
      goToLogin: 'Gå til login',
      resetComplete: 'Adgangskode nulstillet',
      changeComplete: 'Adgangskode ændret',
      redirectingToLogin: 'Din adgangskode er blevet nulstillet. Omdirigerer til login...',
      redirectingToDashboard: 'Din adgangskode er blevet ændret. Omdirigerer til forsiden...',
      resetHeading: 'Nulstil din adgangskode',
      changeHeading: 'Skift din adgangskode',
      welcome: 'Hej, {name}!',
      createPassword: 'Indtast venligst din nye adgangskode.',
      changeMessage: 'Indtast venligst din nuværende adgangskode og vælg en ny.',
      currentPassword: 'Nuværende adgangskode',
      enterCurrentPassword: 'Indtast din nuværende adgangskode',
      newPassword: 'Ny adgangskode',
      enterNewPassword: 'Indtast din nye adgangskode',
      confirmPassword: 'Bekræft adgangskode',
      confirmPasswordPlaceholder: 'Bekræft din nye adgangskode',
      tooShort: 'Adgangskoden skal være mindst 8 tegn',
      mismatch: 'Adgangskoderne matcher ikke',
      resetting: 'Nulstiller adgangskode...',
      changing: 'Ændrer adgangskode...',
      resetButton: 'Nulstil adgangskode',
      changeButton: 'Skift adgangskode',
      wrongCurrentPassword: 'Den nuværende adgangskode er forkert.',
      failed: 'Adgangskoden kunne ikke ændres. Prøv venligst igen.',
    },
    unauthorized: {
      title: 'Adgang nægtet',
      message: 'Du har ikke adgang til denne side.',
      goBack: 'Gå tilbage',
      goHome: 'Gå til forsiden',
    },
    unavailable: {
      title: 'Ikke tilgængelig',
      tenantDisabled: 'Denne funktion er ikke en del af jeres aftale.',
      orgNodeDisabled: 'Denne funktion er slået fra for den valgte afdeling.',
      unknown: 'Denne funktion er ikke tilgængelig lige nu.',
      goHome: 'Gå til forsiden',
    },
    notFound: {
      title: 'Siden findes ikke',
      message: 'Adressen findes ikke i portalen.',
      goHome: 'Gå til forsiden',
    },
    legacy: {
      title: 'Findes i den nuværende portal',
      message: 'Denne side er ikke flyttet til den nye portal endnu.',
      open: 'Åbn i den nuværende portal',
      notLinked: 'Brug den nuværende portal til denne side.',
    },
    header: {
      scopedView: 'Brugervisning',
      userMenu: 'Brugermenu',
      account: 'Konto',
    },
    breadcrumb: { label: 'Brødkrumme', home: 'Forside' },
    footer: { copyright: '© {year}', poweredBy: 'Powered by {brand}' },
    orgNodeSelector: {
      selectOrganization: 'Vælg organisation',
      organization: 'Organisation',
      expand: 'Udvid',
      collapse: 'Fold sammen',
      loading: 'Indlæser...',
      showingDataFor: 'Viser data for',
      types: { ENTERPRISE: 'ENT', DEALER: 'FHL', GROUP: 'GRP', DEPARTMENT: 'AFD' },
    },
    sidebar: {
      navigation: 'Hovedmenu',
      hide: 'Skjul menu',
      show: 'Vis menu',
      navigateTo: 'Naviger til',
      opensNewTab: 'åbner i en ny fane',
      pinned: 'Fastgjort',
      pin: 'Fastgør {label}',
      unpin: 'Frigør {label}',
      badge: '{label}: {count} venter',
      inSection: '{label} ({section})',
    },
    dashboard: {
      welcomeTitle: 'Velkommen',
      welcomeSubtitle: 'Vælg et område i menuen for at komme i gang.',
    },
    profile: {
      changePassword: 'Skift adgangskode',
      testCustomers: 'Testkunder',
      testCustomersMoved: 'Testkunder administreres indtil videre i den nuværende portal.',
    },
    kit: {
      yes: 'Ja',
      no: 'Nej',
      noData: 'Ingen data tilgængelige',
      rowsPerPage: 'Rækker pr. side',
      range: '{start} – {end} af {total}',
      rangeOpen: '{start} – {end}',
      pagination: 'Sideskift',
      firstPage: 'Første side',
      previousPage: 'Forrige side',
      nextPage: 'Næste side',
      lastPage: 'Sidste side',
      selectAll: 'Vælg alle',
      selectRow: 'Vælg række',
      actions: 'Handlinger',
      search: 'Søg...',
      clearSearch: 'Ryd søgning',
      remove: 'Fjern',
      close: 'Luk',
      cancel: 'Annuller',
      save: 'Gem',
      saving: 'Gemmer…',
      delete: 'Slet',
      hide: 'LUK',
      show: 'VIS',
      notifications: 'Notifikationer',
      dismiss: 'Luk besked',
      expandGroup: 'Udvid gruppe',
      collapseGroup: 'Fold gruppe sammen',
      selectedCount: '{selected} af {total} valgt',
      noMatches: 'Ingen resultater',
      mergedCount: '{count} ens poster — vælges altid sammen',
      validation: {
        required: 'Feltet skal udfyldes',
        email: 'Indtast en gyldig e-mailadresse',
        minLength: 'Mindst {n} tegn',
        maxLength: 'Højst {n} tegn',
        min: 'Mindst {n}',
        max: 'Højst {n}',
        pattern: 'Ugyldigt format',
        invalid: 'Ugyldig værdi',
      },
    },
  },
  en: {
    brand: { portalName: 'Dealer Portal', logoAlt: 'MiCar', orgLogoAlt: '{name} logo' },
    feedback: {
      loading: 'Loading...',
      errorMessage: 'An error occurred. Please try again.',
      retry: 'Retry',
    },
    login: {
      title: 'Log in',
      email: 'Email',
      emailPlaceholder: 'Email',
      password: 'Password',
      passwordPlaceholder: 'Password',
      emailInvalid: 'Please enter a valid email address',
      passwordRequired: 'Password is required',
      submit: 'Log in',
      submitting: 'Logging in...',
      forgotPassword: 'Forgot password?',
      wrongCredentials: 'Wrong email or password.',
      accountLocked: 'The account is locked. Contact your administrator.',
      failed: 'An error occurred. Please try again.',
      passwordResetDone: 'Your password has been reset. Log in with the new password.',
      subtitle: 'Log in to your {brand} dealer portal',
      signUpTitle: 'Sign up',
      signUpText: 'Contact us to become one of the dealers who benefit from {brand}',
      signUpAction: 'Contact us now',
    },
    forgotPassword: {
      title: 'Reset password',
      subtitle: "Enter your email address and we'll send you a link to reset your password.",
      email: 'Email address',
      emailPlaceholder: 'Enter your email',
      submit: 'Send reset link',
      submitting: 'Sending...',
      successTitle: 'Check your email',
      successMessage:
        "If an account with that email exists, we've sent password reset instructions.",
      backToLogin: 'Back to login',
    },
    resetPassword: {
      title: 'Reset password',
      changeTitle: 'Change password',
      validating: 'Validating reset link...',
      invalidToken: 'Invalid reset link',
      tokenExpired: 'This password reset link has expired. Please request a new one.',
      tokenAlreadyUsed: 'This password reset link has already been used. Please request a new one.',
      invalidLink:
        'This password reset link is invalid or has been revoked. Please request a new one.',
      goToLogin: 'Go to login',
      resetComplete: 'Password reset complete',
      changeComplete: 'Password changed',
      redirectingToLogin: 'Your password has been reset. Redirecting to login...',
      redirectingToDashboard: 'Your password has been changed. Redirecting to the front page...',
      resetHeading: 'Reset your password',
      changeHeading: 'Change your password',
      welcome: 'Hello, {name}!',
      createPassword: 'Please enter your new password.',
      changeMessage: 'Please enter your current password and choose a new one.',
      currentPassword: 'Current password',
      enterCurrentPassword: 'Enter your current password',
      newPassword: 'New password',
      enterNewPassword: 'Enter your new password',
      confirmPassword: 'Confirm password',
      confirmPasswordPlaceholder: 'Confirm your new password',
      tooShort: 'The password must be at least 8 characters',
      mismatch: 'Passwords do not match',
      resetting: 'Resetting password...',
      changing: 'Changing password...',
      resetButton: 'Reset password',
      changeButton: 'Change password',
      wrongCurrentPassword: 'The current password is wrong.',
      failed: 'The password could not be changed. Please try again.',
    },
    unauthorized: {
      title: 'Access denied',
      message: 'You do not have access to this page.',
      goBack: 'Go back',
      goHome: 'Go to the front page',
    },
    unavailable: {
      title: 'Not available',
      tenantDisabled: 'This feature is not part of your subscription.',
      orgNodeDisabled: 'This feature is switched off for the selected department.',
      unknown: 'This feature is not available right now.',
      goHome: 'Go to the front page',
    },
    notFound: {
      title: 'Page not found',
      message: 'The address does not exist in the portal.',
      goHome: 'Go to the front page',
    },
    legacy: {
      title: 'Available in the current portal',
      message: 'This page has not moved to the new portal yet.',
      open: 'Open in the current portal',
      notLinked: 'Use the current portal for this page.',
    },
    header: {
      scopedView: 'User view',
      userMenu: 'User menu',
      account: 'Account',
    },
    breadcrumb: { label: 'Breadcrumb', home: 'Home' },
    footer: { copyright: '© {year}', poweredBy: 'Powered by {brand}' },
    orgNodeSelector: {
      selectOrganization: 'Select organisation',
      organization: 'Organisation',
      expand: 'Expand',
      collapse: 'Collapse',
      loading: 'Loading...',
      showingDataFor: 'Showing data for',
      types: { ENTERPRISE: 'ENT', DEALER: 'DLR', GROUP: 'GRP', DEPARTMENT: 'DEP' },
    },
    sidebar: {
      navigation: 'Main menu',
      hide: 'Hide menu',
      show: 'Show menu',
      navigateTo: 'Navigate to',
      opensNewTab: 'opens in a new tab',
      pinned: 'Pinned',
      pin: 'Pin {label}',
      unpin: 'Unpin {label}',
      badge: '{label}: {count} waiting',
      inSection: '{label} ({section})',
    },
    dashboard: {
      welcomeTitle: 'Welcome',
      welcomeSubtitle: 'Choose an area in the menu to get started.',
    },
    profile: {
      changePassword: 'Change password',
      testCustomers: 'Test customers',
      testCustomersMoved: 'Test customers are managed in the current portal for now.',
    },
    kit: {
      yes: 'Yes',
      no: 'No',
      noData: 'No data available',
      rowsPerPage: 'Rows per page',
      range: '{start} – {end} of {total}',
      rangeOpen: '{start} – {end}',
      pagination: 'Pagination',
      firstPage: 'First page',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      lastPage: 'Last page',
      selectAll: 'Select all',
      selectRow: 'Select row',
      actions: 'Actions',
      search: 'Search...',
      clearSearch: 'Clear search',
      remove: 'Remove',
      close: 'Close',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
      delete: 'Delete',
      hide: 'HIDE',
      show: 'SHOW',
      notifications: 'Notifications',
      dismiss: 'Dismiss message',
      expandGroup: 'Expand group',
      collapseGroup: 'Collapse group',
      selectedCount: '{selected} of {total} selected',
      noMatches: 'No matches',
      mergedCount: '{count} identical entries — always selected together',
      validation: {
        required: 'This field is required',
        email: 'Please enter a valid email',
        minLength: 'At least {n} characters',
        maxLength: 'At most {n} characters',
        min: 'Minimum value is {n}',
        max: 'Maximum value is {n}',
        pattern: 'Invalid format',
        invalid: 'Invalid value',
      },
    },
  },
  no: {
    brand: { portalName: 'Forhandlerportal', logoAlt: 'MiCar', orgLogoAlt: 'Logo for {name}' },
    feedback: {
      loading: 'Laster...',
      errorMessage: 'Det oppstod en feil. Vennligst prøv igjen.',
      retry: 'Prøv igjen',
    },
    login: {
      title: 'Logg inn',
      email: 'E-post',
      emailPlaceholder: 'E-post',
      password: 'Passord',
      passwordPlaceholder: 'Passord',
      emailInvalid: 'Skriv inn en gyldig e-postadresse',
      passwordRequired: 'Passord er påkrevd',
      submit: 'Logg inn',
      submitting: 'Logger inn...',
      forgotPassword: 'Glemt passord?',
      wrongCredentials: 'Feil e-post eller passord.',
      accountLocked: 'Kontoen er låst. Kontakt administratoren din.',
      failed: 'Det oppstod en feil. Vennligst prøv igjen.',
      passwordResetDone: 'Passordet ditt er tilbakestilt. Logg inn med det nye passordet.',
      subtitle: 'Logg inn på din {brand} forhandlerportal',
      signUpTitle: 'Registrer deg',
      signUpText: 'Kontakt oss for å bli en av forhandlerne som har glede av {brand}',
      signUpAction: 'Ta kontakt nå',
    },
    forgotPassword: {
      title: 'Tilbakestill passord',
      subtitle:
        'Skriv inn e-postadressen din, så sender vi deg en lenke for å tilbakestille passordet.',
      email: 'E-postadresse',
      emailPlaceholder: 'Skriv inn e-posten din',
      submit: 'Send lenke',
      submitting: 'Sender...',
      successTitle: 'Sjekk e-posten din',
      successMessage:
        'Hvis det finnes en konto med den e-posten, har vi sendt instruksjoner for å tilbakestille passordet.',
      backToLogin: 'Tilbake til innlogging',
    },
    resetPassword: {
      title: 'Tilbakestill passord',
      changeTitle: 'Endre passord',
      validating: 'Validerer lenken...',
      invalidToken: 'Ugyldig lenke',
      tokenExpired: 'Denne lenken for å tilbakestille passordet har utløpt. Be om en ny.',
      tokenAlreadyUsed:
        'Denne lenken for å tilbakestille passordet er allerede brukt. Be om en ny.',
      invalidLink:
        'Denne lenken for å tilbakestille passordet er ugyldig eller trukket tilbake. Be om en ny.',
      goToLogin: 'Gå til innlogging',
      resetComplete: 'Passordet er tilbakestilt',
      changeComplete: 'Passordet er endret',
      redirectingToLogin: 'Passordet ditt er tilbakestilt. Sender deg til innlogging...',
      redirectingToDashboard: 'Passordet ditt er endret. Sender deg til forsiden...',
      resetHeading: 'Tilbakestill passordet ditt',
      changeHeading: 'Endre passordet ditt',
      welcome: 'Hei, {name}!',
      createPassword: 'Skriv inn det nye passordet ditt.',
      changeMessage: 'Skriv inn det nåværende passordet ditt og velg et nytt.',
      currentPassword: 'Nåværende passord',
      enterCurrentPassword: 'Skriv inn det nåværende passordet ditt',
      newPassword: 'Nytt passord',
      enterNewPassword: 'Skriv inn det nye passordet ditt',
      confirmPassword: 'Bekreft passord',
      confirmPasswordPlaceholder: 'Bekreft det nye passordet ditt',
      tooShort: 'Passordet må være minst 8 tegn',
      mismatch: 'Passordene er ikke like',
      resetting: 'Tilbakestiller passord...',
      changing: 'Endrer passord...',
      resetButton: 'Tilbakestill passord',
      changeButton: 'Endre passord',
      wrongCurrentPassword: 'Det nåværende passordet er feil.',
      failed: 'Passordet kunne ikke endres. Vennligst prøv igjen.',
    },
    unauthorized: {
      title: 'Ingen tilgang',
      message: 'Du har ikke tilgang til denne siden.',
      goBack: 'Gå tilbake',
      goHome: 'Gå til forsiden',
    },
    unavailable: {
      title: 'Ikke tilgjengelig',
      tenantDisabled: 'Denne funksjonen er ikke en del av avtalen deres.',
      orgNodeDisabled: 'Denne funksjonen er slått av for den valgte avdelingen.',
      unknown: 'Denne funksjonen er ikke tilgjengelig akkurat nå.',
      goHome: 'Gå til forsiden',
    },
    notFound: {
      title: 'Siden finnes ikke',
      message: 'Adressen finnes ikke i portalen.',
      goHome: 'Gå til forsiden',
    },
    legacy: {
      title: 'Finnes i den nåværende portalen',
      message: 'Denne siden er ikke flyttet til den nye portalen ennå.',
      open: 'Åpne i den nåværende portalen',
      notLinked: 'Bruk den nåværende portalen for denne siden.',
    },
    header: {
      scopedView: 'Brukervisning',
      userMenu: 'Brukermeny',
      account: 'Konto',
    },
    breadcrumb: { label: 'Brødsmuler', home: 'Forside' },
    footer: { copyright: '© {year}', poweredBy: 'Powered by {brand}' },
    orgNodeSelector: {
      selectOrganization: 'Velg organisasjon',
      organization: 'Organisasjon',
      expand: 'Utvid',
      collapse: 'Slå sammen',
      loading: 'Laster...',
      showingDataFor: 'Viser data for',
      types: { ENTERPRISE: 'ENT', DEALER: 'FHL', GROUP: 'GRP', DEPARTMENT: 'AVD' },
    },
    sidebar: {
      navigation: 'Hovedmeny',
      hide: 'Skjul meny',
      show: 'Vis meny',
      navigateTo: 'Naviger til',
      opensNewTab: 'åpnes i en ny fane',
      pinned: 'Festet',
      pin: 'Fest {label}',
      unpin: 'Løsne {label}',
      badge: '{label}: {count} venter',
      inSection: '{label} ({section})',
    },
    dashboard: {
      welcomeTitle: 'Velkommen',
      welcomeSubtitle: 'Velg et område i menyen for å komme i gang.',
    },
    profile: {
      changePassword: 'Endre passord',
      testCustomers: 'Testkunder',
      testCustomersMoved: 'Testkunder administreres foreløpig i den nåværende portalen.',
    },
    kit: {
      yes: 'Ja',
      no: 'Nei',
      noData: 'Ingen data tilgjengelig',
      rowsPerPage: 'Rader per side',
      range: '{start} – {end} av {total}',
      rangeOpen: '{start} – {end}',
      pagination: 'Sidebytte',
      firstPage: 'Første side',
      previousPage: 'Forrige side',
      nextPage: 'Neste side',
      lastPage: 'Siste side',
      selectAll: 'Velg alle',
      selectRow: 'Velg rad',
      actions: 'Handlinger',
      search: 'Søk...',
      clearSearch: 'Tøm søk',
      remove: 'Fjern',
      close: 'Lukk',
      cancel: 'Avbryt',
      save: 'Lagre',
      saving: 'Lagrer…',
      delete: 'Slett',
      hide: 'SKJUL',
      show: 'VIS',
      notifications: 'Varsler',
      dismiss: 'Lukk melding',
      expandGroup: 'Utvid gruppe',
      collapseGroup: 'Slå sammen gruppe',
      selectedCount: '{selected} av {total} valgt',
      noMatches: 'Ingen treff',
      mergedCount: '{count} like oppføringer — velges alltid sammen',
      validation: {
        required: 'Feltet må fylles ut',
        email: 'Skriv inn en gyldig e-postadresse',
        minLength: 'Minst {n} tegn',
        maxLength: 'Maks {n} tegn',
        min: 'Minst {n}',
        max: 'Maks {n}',
        pattern: 'Ugyldig format',
        invalid: 'Ugyldig verdi',
      },
    },
  },
};

/**
 * The catalogue locale for a language code: the backend's `dk`/`en`/`no`, or a browser's
 * `da-DK`, `nb-NO`, `en-GB`. Danish unless it is clearly English or Norwegian — the portal's
 * users are Danish dealers first.
 */
export function shellLocale(language: string | null | undefined): ShellLocale {
  const lower = (language ?? '').toLowerCase();
  if (lower.startsWith('en')) {
    return 'en';
  }
  if (
    lower === 'no' ||
    lower.startsWith('nb') ||
    lower.startsWith('nn') ||
    lower.startsWith('no-')
  ) {
    return 'no';
  }
  return 'dk';
}

/** Replace `{key}` placeholders. */
export function fill(text: string, values: Record<string, string>): string {
  return text.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match);
}
